import { graphql } from '~/graphql/generated/gql';

/**
 * The questionnaire a student is being asked, shared by the two documents that ask for it.
 *
 * `LearningFeedbackSurveyDefinition` has no `id`, so graphcache stores it as a keyless object
 * embedded in the collection entity. Two selections of different shapes writing that same field
 * would leave whichever wrote last deciding what the other can read, so the page query and the
 * completion mutation payload have to select identically — hence one fragment, not two lists.
 */
export const feedbackSurveyDefinitionFragment = graphql(`
  fragment FeedbackSurveyDefinitionFields on LearningFeedbackSurveyDefinition {
    sharedWithTeacher
    askedAtCompletion
    lessonAudience
    questions {
      key
      label
      labelKey
      required
      type
      ... on LearningFeedbackSurveyQuestionScale {
        min
        max
        minLabel
        maxLabel
      }
      ... on LearningFeedbackSurveyQuestionDropdown {
        options {
          value
          label
          labelKey
        }
      }
      ... on LearningFeedbackSurveyQuestionLikert {
        likertOptions
      }
    }
  }
`);

export const SUBMIT_LEARNING_FEEDBACK_SURVEY = graphql(`
  mutation submitLearningFeedbackSurvey(
    $input: SubmitLearningFeedbackSurveyInput!
  ) {
    submitLearningFeedbackSurvey(input: $input) {
      coinsAwarded
      query {
        core {
          user {
            self {
              id
              latestCoinBalance
            }
          }
        }
      }
    }
  }
`);

export const FEEDBACK_SURVEY_COLLECTION_DETAIL = graphql(`
  query feedbackSurveyCollectionDetail($filters: AdminLearningAssetCollectionFilters) {
    learningAssetCollectionsList(first: 1, filters: $filters) {
      data {
        id
        name
        collectionType
        internalFeedbackSurveyResponsesCount
        feedbackSurvey {
          ...FeedbackSurveyDefinitionFields
        }
        authoredFeedbackSurvey {
          ...FeedbackSurveyAdminFields
        }
      }
    }
  }
`);

export const FEEDBACK_SURVEY_LEARNING_ASSET_DETAIL = graphql(`
  query feedbackSurveyLearningAssetDetail($filters: AdminLearningAssetFilters) {
    learningAssetsList(first: 1, filters: $filters) {
      data {
        id
        name
        internalFeedbackSurveyResponsesCount
        feedbackSurvey {
          ...FeedbackSurveyDefinitionFields
        }
        authoredFeedbackSurvey {
          ...FeedbackSurveyAdminFields
        }
      }
    }
  }
`);

export const LEARNING_FEEDBACK_SURVEY_RESPONSES = graphql(`
  query learningFeedbackSurveyResponses(
    $collectionId: ID
    $learningAssetId: ID
    $deliveryType: FeedbackSurveyDeliveryType
    $first: Int
    $page: Int
  ) {
    learningFeedbackSurveyResponses(
      collectionId: $collectionId
      learningAssetId: $learningAssetId
      deliveryType: $deliveryType
      first: $first
      page: $page
    ) {
      data {
        id
        user {
          id
          primaryEmail
        }
        responses
        coinsAwarded
        updatedAt
      }
      paginatorInfo {
        currentPage
        lastPage
        total
      }
    }
  }
`);
